import {Module} from 'vuex';
import * as firebase from 'firebase/app';
import 'firebase/firestore';
import moment from 'moment';
import {serializer} from '../../api/shared/serializer';
import {ResidentStatus} from '@/enums/ResidentStatus';
import {FamilyHubUserStatus, FamilyHubUserStatusReason} from '@/enums/FamilyHubUserStatus';
import {MemberInviteStatuses} from '@/enums/MemberInviteStatuses';
import {FamilyHubAppStatus} from '@/enums/FamilyHubAppStatus';
import {getRegionName} from '@/utils/functionsRegion';
import {ResidentParams} from "@/consts/ResidentParams";
import {UserTypeEnum} from '@/enums/UserTypeEnum';
import {DEFAULT_AVATAR_WHITE, DEFAULT_AVATAR_WHITE_200x200} from '@/consts/default-avatar';
import {EventsTypes} from '@/enums/EventsTypes';
import {ResidentsFilters} from "@/models/resident/ResidentsFilters";
import { getFormattedDateTimeString } from '@/utils/dateTimeFormat';
import * as Sentry from "@sentry/vue";
import { getDefaultFamilyResidentAccess, mapFamilyAccessSettings } from '@/utils/familyResidentAccess';
import { DataTypes } from '@/enums/DataTypes';
import { FamilyResidentSettings } from '@/enums/FamilyResidentSettings';
import { defaultFacilityValues } from '@/consts/defaultFacilityValues';
import { CallsTypes } from '@/enums/CallsTypes';
import { AuditLogsTier1 as Tier1, AuditLogsTier2 as Tier2, AuditLogsTier3 as Tier3 } from '@/enums/AuditLogs';
import { toJsDateIfTs } from '@/utils/toJsDateIfTs';
import { trimStringFields } from '@/utils/stringFormat';

const residentModule: Module<any, any> = {
    namespaced: false,
    state: {
        residents: [],
        residentsShortData: [],
        currentResident: null,
        residentFamilyMembers: [],
        familyMembers: [],
        familyMember: null,
        residentLogs: [],
        residentsWithUpcomingBirthdays: [],
        leastEngagedResidents: [],
        onResidentsUnsubscribe: null,
        mainResidentsFilters: new ResidentsFilters(),
        upcomingCallsByResident: [],
    },

    mutations: {
        setResidents(state: any, payload: []) {
            state.residents = payload;
        },

        setResidentsShortData(state: any, payload: []) {
            state.residentsShortData = payload;
        },

        setCurrentResident(state: any, payload: any) {
            state.currentResident = payload;
        },

        setAllFamilyMembers(state: any, payload: []) {
            state.familyMembers = payload;
        },

        setResidentFamilyMembers(state: any, payload: []) {
            state.residentFamilyMembers = payload;
        },

        setCurrentFamilyMember(state: any, payload: []) {
            state.familyMember = payload;
        },

        setCurrencyFamilyMemberVisits(state: any, payload: []) {
            state.familyMember.recentVisits = payload;
        },

        setResidentLogs(state: any, payload: []) {
            state.residentLogs = payload;
        },

        setResidentsWithUpcomingBirthdays(state: any, payload: []) {
            state.residentsWithUpcomingBirthdays = payload;
        },

        setLeastEngagedResidents(state: any, payload: []) {
            state.leastEngagedResidents = payload;
        },

        setFamilyMemberInvitations(state: any, payload: []) {
            state.familyMemberInvitations = payload;
        },

        addResidentLogs(state: any, payload: []) {
            state.residentLogs = [...state.residentLogs, ...payload];
        },
        setMainResidentsFilters(state: any, payload: any) {
            const filters = state.mainResidentsFilters;

            if (payload?.hasOwnProperty('wingName')) {
                filters.wingName = payload.wingName;
            }

            state.mainResidentsFilters = filters;
        },
        setUpcomingCallsByResident(state: any, payload: []) {
            state.upcomingCallsByResident = payload;
        },
    },

    actions: {

        async getResidentsShortData({commit, dispatch}, status) {

            let devices = this.state.devicesModule.devices

            if (!devices?.length) {
                devices = await dispatch("getDevices");
            }

            return await firebase
                .app()
                .functions(getRegionName())
                .httpsCallable("getResidentsShortData")({
                    facilityId: this.state.authModule.currentFacility,
                    status: status,
                    params: ResidentParams
                })
                .then(async (data: any) => {
                    const residents = data.data.data.residents as [];

                    commit("setResidentsShortData", residents)

                    return residents
                })
        },

        async getFamilyMembers({ commit }, data) {

            let query = firebase
                .firestore()
                .collection('familyHubUsers')
                .where('facilityId', '==', this.state.authModule.currentFacility)

            if(data?.start && data?.end){
                query = query.where("createdDateTime", ">=", moment(data.start).toDate())
                .where("createdDateTime", "<", moment(data.end).add(1, "day").toDate());
            }

            await query
                .get()
                .then(querySnapshot => {
                    const facility = this.state.facilityModule.selectedFacility
                    const familyMembers = querySnapshot.docs.map(familyMemberSnap => {
                    const response = familyMemberSnap.data();
                    const lastActive = response?.lastActive?.seconds? new Date(response?.lastActive.seconds * 1000): null
                    response.id = familyMemberSnap.id;
                    if(lastActive){
                        response.lastActiveShortDisplay = getFormattedDateTimeString(lastActive, facility?.dateFormat, facility?.timeFormat, false) 
                        response.lastActiveFullDisplay = getFormattedDateTimeString(lastActive, facility?.dateFormat, facility?.timeFormat, true) 
                    }
                    else{
                        response.lastActiveShortDisplay = '-'
                        response.lastActiveFullDisplay = '-'
                    }

                    return { ...response }
                });

                commit('setAllFamilyMembers', familyMembers);
            });
        },

        async getFamilyMembersByResident({commit}, {residentId}) {

            const orgId = this.state.facilityModule.selectedFacility.orgId

            await firebase.app().functions(getRegionName())
                .httpsCallable("groupC-getFamilyMembersByResidentId")({
                    orgId: orgId,
                    residentId: residentId
                })
                .then(snap => {
                    console.log()
                    const data = snap?.data?.data || []
                    commit('setResidentFamilyMembers', data);

                }).catch((error) => {
                    console.error(error)
                    commit('setResidentFamilyMembers', []);
                })

        },

        async getFamilyMember({commit, dispatch, state}, {id, residentId}) {
            const familyMember = state.familyMembers.find(member => member.id === id);

            if (familyMember) {
                commit('setCurrentFamilyMember', familyMember);

                if (residentId) {
                   await dispatch('getResident', residentId);
                }

               await dispatch('getFamilyMemberVisits', familyMember);

               return familyMember;
            } else {

              return await firebase
                    .firestore()
                    .collection('familyHubUsers')
                    .doc(id)
                    .get()
                    .then(async querySnapshot => {
                        const familyMember = querySnapshot.data();
                        const facility = this.state.facilityModule.selectedFacility
                        
                        if (querySnapshot.exists) {
                            familyMember.id = querySnapshot.id;
  
                            const lastActive = familyMember?.lastActive?.seconds? new Date(familyMember?.lastActive.seconds * 1000): null
                            if(lastActive){
                                familyMember.lastActiveShortDisplay = getFormattedDateTimeString(lastActive, facility.dateFormat || defaultFacilityValues.dateFormat, facility.timeFormat || defaultFacilityValues.timeFormat, false)
                                familyMember.lastActiveFullDisplay = getFormattedDateTimeString(lastActive, facility.dateFormat || defaultFacilityValues.dateFormat, facility.timeFormat || defaultFacilityValues.timeFormat, true)
                            }
                            else{
                                familyMember.lastActiveShortDisplay = '-'
                                familyMember.lastActiveFullDisplay = '-'
                            }
                            commit('setCurrentFamilyMember', familyMember);
                            await dispatch('getResident', familyMember.residentId);
                            await dispatch('getFamilyMemberVisits', familyMember);

                            return familyMember;
                        } else {
                            return null;
                        }
                    });
            }
        },

        async getResidentWithUpcomingBirthdays({commit}) {
            try {
                const period = [
                    moment().add(-1, "days").locale('en-gb').format("MMM-DD"),
                    moment().locale('en-gb').format("MMM-DD"),
                    moment().add(1, "days").locale('en-gb').format("MMM-DD"),
                    moment().add(2, "days").locale('en-gb').format("MMM-DD"),
                    moment().add(3, "days").locale('en-gb').format("MMM-DD"),
                    moment().add(4, "days").locale('en-gb').format("MMM-DD"),
                    moment().add(5, "days").locale('en-gb').format("MMM-DD"),
                    moment().add(6, "days").locale('en-gb').format("MMM-DD"),
                    moment().add(7, "days").locale('en-gb').format("MMM-DD")
                ];

                const querySnapshot = await firebase
                    .firestore()
                    .collection("residents")
                    .where("facilityId", "==", this.state.authModule.currentFacility)
                    .where("status", "==", ResidentStatus.ActiveResident)
                    .where("shortDob", "in", period)
                    .orderBy("dateOfBirth")
                    .get();

                const today = moment().startOf('day');

                const res = querySnapshot.docs.map((doc) => {
                    const resident = doc.data();
                    const birthday = moment(resident.dateOfBirth.seconds * 1000).startOf('day');
                    const nextBirthday = birthday.clone().year(today.year());
                    const age = nextBirthday.diff(birthday, "years");
                    resident.dateOfBirth = moment(resident.dateOfBirth.seconds * 1000).toDate();
                    resident.age = age;
                    resident.id = doc.id;
                    return resident;
                });

                commit('setResidentsWithUpcomingBirthdays', res);
            } catch (error) {
                console.error("Unable to get residents upcoming birthdays", error);
            }
        },

        async getLeastEngagedResidents({commit}) {
            const fromDate = moment().add(-1, "months").toDate();
            const toDate = moment().toDate();
            const facilityId = this.state.authModule.currentFacility;
            const residents = this.state.residentModule.residentsShortData
            const types = [
                EventsTypes.Concierge,
                EventsTypes.ConciergeRequest,
                EventsTypes.FamilyVisit,
                EventsTypes.LifestylePrograms,
                EventsTypes.PremiumServices,
            ];

            const snap = await firebase
                .firestore()
                .collection("calender")
                .where("facilityId", "==", facilityId)
                .where("start", ">=", fromDate)
                .where("start", "<=", toDate)
                .where("type", "in", types)
                .get();

            let leastEngagedResidents = []

            const calenderEvents: any[] = serializer.querySnapshotToDocuments(snap).filter((x: any) => Object.prototype.hasOwnProperty.call(x, 'attendeesIds'));

            console.log(`leastEngagedResidents.events: ${calenderEvents.length}`)

            if (calenderEvents.length > 0) {
                // Create a map to store attendance count for each resident
                const attendanceCountMap = new Map();

                // Iterate through calendar events to count attendance for each resident
                for (const event of calenderEvents) {
                    const attendeesIds = event.attendeesIds || [];
                    for (const residentId of attendeesIds) {
                        attendanceCountMap.set(residentId, (attendanceCountMap.get(residentId) || 0) + 1);
                    }
                }

                // Iterate through residents to add residents without attendance
                residents.forEach((resident) => {
                    if (!attendanceCountMap.has(resident.id)) {
                        attendanceCountMap.set(resident.id, 0);
                    }
                });

                // Sort residents based on attendance count (least to most engaged)
                const residentsWithAttendance = Array.from(attendanceCountMap.entries()).map(([residentId, attendanceCount]) => ({
                    residentId,
                    attendanceCount,
                }));

                residentsWithAttendance.sort((a, b) => a.attendanceCount - b.attendanceCount);

                leastEngagedResidents = residentsWithAttendance.map((item) => {
                    const residentInfo = residents.find((resident) => resident.id === item.residentId) || {};
                    return {
                        residentId: item.residentId,
                        attendanceCount: item.attendanceCount,
                        name: residentInfo.name || "",
                        pname: residentInfo.pname || "",
                        thumbnailUrl: residentInfo.thumbnailUrl || DEFAULT_AVATAR_WHITE_200x200
                    };
                }).filter((x) => x.name.length > 0);
            }

            commit('setLeastEngagedResidents', leastEngagedResidents);
        },

        async updateFamilyMember({dispatch}, {id, data}) {

            trimStringFields(data);
            data.updatedById = this.state.authModule.user.id;
            data.updatedByType = UserTypeEnum.Staff;
            data.typesOfRecordChanged = [`${Tier1.FamilySettings}: ${Tier2.MainContact}: ${Tier3.Amend}`]
            
            await firebase
                .firestore()
                .collection('familyHubUsers')
                .doc(id)
                .update({...data})

            await dispatch('getFamilyMembers');
            dispatch('getFamilyMember', {id: id});
            dispatch('updateFamilyMemberApprovalRequests', {id, data});
        },

        async updateFamilyMemberApprovalRequests(_, {id, data}) {
            const getApprovalRequests = firebase
                .firestore()
                .collection("additionalServiceRequestApprovals")
                .where("facilityId", "==", this.state.authModule.currentFacility)
                .where("familyHubMemberId", "==", id)

            const approvalRequests = await getApprovalRequests.get().then(querySnapshot => {
                return (querySnapshot.docs.map(x => ({
                    id: x.id,
                    ...x.data(),
                })) as any[])
            })

            for (let i = 0; i < approvalRequests.length; i++) {
                await firebase
                    .firestore()
                    .collection('additionalServiceRequestApprovals')
                    .doc(approvalRequests[i].id)
                    .update({
                        familyHubMemberName: data.name,
                    })
            }
        },

        async getFamilyMemberInvitationsByDates(
            {commit},
            {start = moment(), end = moment()}
        ) {
            const createdWithoutInvitationQuery = firebase
                .firestore()
                .collection("familyHubUsers")
                .where("facilityId", "==", this.state.authModule.currentFacility)
                .where("createdDateTime", ">=", moment(start).toDate())
                .where("createdDateTime", "<", moment(end).add(1, "day").toDate())

            const invitationsQuery = firebase
                .firestore()
                .collection("familyHubUsers")
                .where("facilityId", "==", this.state.authModule.currentFacility)
                .where("invitationSentDateTime", ">=", moment(start).toDate())
                .where("invitationSentDateTime", "<", moment(end).add(1, "day").toDate())

            const createdWithoutInvitationList = await createdWithoutInvitationQuery.get().then(querySnapshot => {
                return (querySnapshot.docs.map(x => ({
                    id: x.id,
                    ...x.data(),
                    invitationDateTime: moment(x.data().createdDateTime.seconds * 1000).toDate(),
                    status: MemberInviteStatuses.CreatedWithoutInvitation
                })) as any[])
                    .filter((x) => x.invitedToFamilyApp == null || x.invitedToFamilyApp == false);
            });

            const invitationsSentList = await invitationsQuery.get().then(querySnapshot => {
                return (querySnapshot.docs.map(x => ({
                    id: x.id,
                    ...x.data(),
                    invitationDateTime: moment(x.data().createdDateTime.seconds * 1000).toDate(),
                    status: MemberInviteStatuses.InvitationSent
                })) as any[])
                    .filter((x) => x.setPasswordToken && x.setPasswordToken.length > 0 && x.invitedToFamilyApp == true);
            });


            const invitationsAcceptedList = await invitationsQuery.get().then(querySnapshot => {
                return (querySnapshot.docs.map(x => ({
                    id: x.id,
                    ...x.data(),
                    invitationDateTime: moment(x.data().createdDateTime.seconds * 1000).toDate(),
                    status: MemberInviteStatuses.InvitationAccepted
                })) as any[])
                    .filter((x) => x.passwordSetDateTime != null);

            });

            const invitations = createdWithoutInvitationList.concat(invitationsAcceptedList).concat(invitationsSentList)

            commit("setFamilyMemberInvitations", invitations);
        },

        async getFamilyMemberVisits({commit}, familyMember) {
            const twoWeeksAgo = moment()
                .add(-14, 'days')
                .toDate();
            const tomorrow = moment()
                .add(1, 'days')
                .toDate();

            const visits = firebase
                .firestore()
                .collection('guestbookFamilyVisits')
                .where('facilityId', '==', this.state.authModule.currentFacility)
                .where('familyHubMemberId', '==', familyMember.id)
                .where('start', '>', twoWeeksAgo)
                .where('start', '<', tomorrow);

            await visits.get().then(querySnapshot => {
                const resList: Array<any> = [];
                querySnapshot.docs.forEach(x => {
                    const thisRes = x.data();
                    resList.push(thisRes);
                });
                commit('setCurrencyFamilyMemberVisits', resList);
            });

            //var chatActivity = function get chat count last fortnight.
        },

        async getResident({commit}, id) {
            if (!this.state?.currentResident || this.state.currentResident?.id !== id) {
                console.log(`getResident(${id})`)
                const familyMembers = await firebase
                    .firestore()
                    .collection('familyHubUsers')
                    .where('facilityId', '==', this.state.authModule.currentFacility)
                    .where('residentIds', 'array-contains', id)
                    .get()
                    .then(qs => {
                        return qs.docs.map(doc => ({
                            id: doc.id,
                            ...doc.data(),
                        }))
                        .filter((x: any) => Array.isArray(x.residentIds) && x.residentIds.length > 0);
                    });

                const resident = firebase
                    .firestore()
                    .collection('residents')
                    .doc(id);

                return await resident.get().then(querySnapshot => {
                    const currentRes = querySnapshot.data();
                    if (currentRes != null) {
                        currentRes.id = querySnapshot.id;
                        currentRes.familyHubUsers = familyMembers || [];
                        currentRes.familyHubUsersIds = currentRes?.familyHubUsersIds?.filter(x=>x?.length > 0) || []

                        commit('setCurrentResident', currentRes);
                        return currentRes
                    }
                    return null
                });
            }
        },

        async getUpcomingCallsByResident({ commit }, { residentId } ) {
            const fromDate = moment().toDate();
            const toDate = moment().add(1, "months").toDate();
        
            const scheduledCalls = (await firebase
                .firestore()
                .collection("calls")
                .where("facilityId", "==", this.state.authModule.currentFacility)
                .where("scheduledAt", ">=", fromDate)
                .where("scheduledAt", "<=", toDate)
                .where("callType", "==", CallsTypes.ScheduledCall).get()).docs
                .map((x: any) => {
                    const data = x.data();
                    return {
                        ...data,
                        id: x.id,
                        start: moment(data.scheduledAt.seconds * 1000).toDate(),
                        end: moment(data.scheduledAt.seconds * 1000).add(1, 'h').toDate(),
                    }
                });
        
            commit("setUpcomingCallsByResident", scheduledCalls);
        },

        async residentDeparture({dispatch}, residentId: any) {
            console.log('depart resident: ' + residentId)

            const response = await firebase.app().functions(getRegionName()).httpsCallable("resident-depart")({
                residentId: residentId,
                updatedById: this.state.authModule.user.id,
                updatedByType: UserTypeEnum.Staff,
                typesOfRecordChanged: [`${Tier1.StaffHubSettings}: ${Tier2.StaffSettings}: ${Tier3.ResidentDeparturesTransfers}`]
            })

            if (response.data.status === 200) {
                console.log('unassigning and deleting departed resident devices')
                await firebase
                    .firestore()
                    .collection('devices')
                    .where('facilityId', '==', this.state.authModule.currentFacility)
                    .where('userId', '==', residentId)
                    .get()
                    .then(async (deviceSnapshot) => {
                        if (deviceSnapshot.docs && deviceSnapshot.docs.length) {
                            for (let i = 0; i < deviceSnapshot.docs.length; i++) {
                                await deviceSnapshot.docs[i].ref.delete();
                            }

                            return true
                        }

                        await dispatch("getDevices");
                        await dispatch('getResident', residentId);
                        await dispatch("getDevicesByResidentId", residentId);
                        return true;
                    })
            }
            else{
                throw new Error('residentDeparture error')
            }
        },
        async undepartResident({dispatch}, {residentId, isUnarchiveMembers}) {
            console.log('undepart resident: ' + residentId)
            const response = await firebase.app().functions(getRegionName()).httpsCallable("resident-undepart")({
                residentId: residentId, 
                isUnarchiveMembers: isUnarchiveMembers,
                updatedById: this.state.authModule.user.id,
                updatedByType: UserTypeEnum.Staff,
                typesOfRecordChanged: [`${Tier1.StaffHubSettings}: ${Tier2.StaffSettings}: ${Tier3.ResidentDeparturesTransfers}`]
            })

            if (response.data.status === 200) {
                await dispatch('getResident', residentId);
            }
            else{
                throw new Error('undepartResident error')
            }
        },
        async residentTransfer({dispatch}, data) {
            return await firebase.app().functions(getRegionName())
                .httpsCallable('transferResident')({
                    residentId: data.residentId,
                    newFacilityId: data.facilityId,
                    updatedById: this.state.authModule.user.id,
                    updatedByType: UserTypeEnum.Staff,
                    typesOfRecordChanged: [`${Tier1.StaffHubSettings}: ${Tier2.StaffSettings}: ${Tier3.ResidentDeparturesTransfers}`]
                })
                .then(snap => {
                    return snap.data;
                });
        },

        async deleteResident(_, residentId) {

            if (!residentId) return

            Sentry.captureMessage(`Deleting resident ${residentId}`);

            let facilityId = null;
            let residentFamilyMembersDocs = null;
            let residentFamilyMembers = null;
            let familyMembersIds = [];
            let residentsOfFamilyMembers = [];

            try {
                facilityId = this.state.authModule.currentFacility;
                residentFamilyMembersDocs = await firebase.firestore().collection('familyHubUsers').where('facilityId', '==', facilityId).where('residentIds', 'array-contains', residentId).get()
                residentFamilyMembers = serializer.querySnapshotToDocuments<any>(residentFamilyMembersDocs)
                familyMembersIds = residentFamilyMembers.map(x => x.id)

                if (familyMembersIds.length) {
                    const residentsOfFamilyMembersDocs = await firebase.firestore().collection('residents').where('facilityId', '==', facilityId).where('familyHubUsersIds', 'array-contains-any', familyMembersIds).get()
                    residentsOfFamilyMembers = serializer.querySnapshotToDocuments<any>(residentsOfFamilyMembersDocs)
                }
            }
            catch (error){
                Sentry.captureException(error);
            }
            finally {
                Sentry.captureMessage(`familyMembersIds: ${familyMembersIds.join(', ')}`);
                Sentry.captureMessage(`residentsOfFamilyMembers: ${residentsOfFamilyMembers.join(', ')}`)
            }

            const residentDocRef = firebase.firestore().collection('residents').doc(residentId)
            await residentDocRef.update({
                deletedById: this.state.authModule.user.id,
                deletedByType: UserTypeEnum.Staff,
                typesOfRecordChanged: [`${Tier1.Residents}: ${Tier2.Resident}: ${Tier3.Remove}`]
            })

            await firebase
                .firestore()
                .runTransaction(async (transaction) => {

                    const residentDoc = await transaction.get(residentDocRef)

                    if (residentDoc.exists) {

                        const residentData = residentDoc.data()


                        if (residentData?.familyHubUsersIds?.length) {

                            for (const famId of residentData.familyHubUsersIds) {
                                const familyMemberDoc = residentFamilyMembersDocs.docs.find(x => x.id === famId)
                                if (familyMemberDoc) {
                                    const hasOtherActiveResident = residentsOfFamilyMembers.some(x => x.id !== residentId && x.familyHubUsersIds.includes(famId) && x.status === ResidentStatus.ActiveResident)
                                    const familyMemberData = residentFamilyMembers.find(x => x.id === famId)
                                    const familyMemberResidents = familyMemberData?.residents ? familyMemberData.residents.filter(x => x.id !== residentId) : [];
                                    const familyMemberResidentsIds = familyMemberData?.residentIds ? familyMemberData.residentIds.filter(x => x !== residentId) : [];
                                    const familyMemberFinancialEPOA = familyMemberData?.financialEPOA ? familyMemberData.financialEPOA.filter(x => x.id !== residentId) : [];
                                    const residentIdObsoleteField = familyMemberData.residentId == residentId ? null : familyMemberData.residentId
                                    const residentNameObsoleteField = residentIdObsoleteField == null ? '' : familyMemberData.residentName

                                    if (hasOtherActiveResident) {
                                        transaction.update(familyMemberDoc.ref, {
                                            residentName: residentNameObsoleteField,
                                            residentId: residentIdObsoleteField,
                                            residents: familyMemberResidents || null,
                                            residentIds: familyMemberResidentsIds || null,
                                            financialEPOA: familyMemberFinancialEPOA || null
                                        })
                                    } else {
                                        transaction.update(familyMemberDoc.ref, {
                                            residentName: residentNameObsoleteField,
                                            residentId: residentIdObsoleteField,
                                            residents: familyMemberResidents || null,
                                            residentIds: familyMemberResidentsIds || null,
                                            financialEPOA: familyMemberFinancialEPOA || null,
                                            appStatus: FamilyHubAppStatus.ReadOnly,
                                            status: FamilyHubUserStatus.Archived,
                                            statusReason: FamilyHubUserStatusReason.ResidentDeleted
                                        })
                                    }
                                }
                            }
                        }

                        transaction.delete(residentDocRef)

                        const devices = await firebase.firestore().collection('devices').where('facilityId', '==', facilityId).where('userId', '==', residentDoc.id).get()

                        if (devices.size > 0) {
                            devices.docs.forEach(deviceRef => {
                                transaction.delete(deviceRef.ref)
                            });
                        }
                    }

                }).catch(e => {
                    console.error(e);
                    Sentry.captureException(e);
                });
        },

        async unAssignDevice({dispatch}, {userId, deviceId, userType}) {
            console.log(`unAssignDevice(${userId} , ${deviceId})`)

            const facilityId = this.state.authModule.currentFacility;

            const deviceSnap = await firebase.firestore().collection('devices').doc(deviceId).get()

            const device = deviceSnap.exists ? deviceSnap.data() : null

            if (!deviceSnap.exists || device.facilityId != facilityId) {
                throw new Error('Device not found')
            }

            if (deviceSnap.data().userId != userId) {
                throw new Error('Device not assigned to this user')
            }

            await deviceSnap.ref.update({
                deletedById: this.state.authModule.user.id,
                deletedByType: UserTypeEnum.Staff,
                typesOfRecordChanged: [`${Tier1.Settings}: ${Tier2.Kiosk}: ${Tier3.UnassignDevice}`]
            });

            return await deviceSnap.ref
                .delete().then(async () => {
                    await dispatch("getDevices");

                    if (userType === UserTypeEnum.Resident) {
                        await dispatch('getResident', userId);
                        await dispatch("getDevicesByResidentId", userId);
                    }
                    return true
                }).catch((e) => {
                    console.error(e)
                    return false
                })
        },

        async deleteFamilyHubUserFromResident({state, dispatch}, payload) {
            if (!payload || !payload.familyHubUserId) {
                console.error('No familyHubUserId provided');
                return;
            }

            payload.updatedById = this.state.authModule.user.id;
            payload.updatedByType = UserTypeEnum.Staff;
            payload.typesOfRecordChanged = [`${Tier1.Residents}: ${Tier2.FamilyMember}: ${Tier3.Remove}`]

            const response = await firebase.app().functions(getRegionName())
                .httpsCallable("familyUser-deleteFamilyUserFromResident")(payload)

            if (response.data.data.status === 200) {
                 return dispatch('getResident', state.currentResident.id)
            } else {
                throw new Error(`Unable to create family member`)
            }
        },

        async deleteFamilyHubUserFromProfile({state, dispatch}, id) {
            if (!id) return

            const ref = firebase.firestore().collection('familyHubUsers').doc(id)

            await ref.update({
                deletedById: this.state.authModule.user.id,
                deletedByType: UserTypeEnum.Staff,
                typesOfRecordChanged: [`${Tier1.FamilyMembers}: ${Tier2.FamilyMember}: ${Tier3.Remove}`]
            }).then(()=>{
                ref.delete()
            })
        },

        async addFamilyHubUser({state, dispatch}, data) {

            if (!data || !data.familyHubUser || !data.residentId) throw new Error('Invalid input parameters')

            const newFamilyHubUser = data.familyHubUser
            trimStringFields(newFamilyHubUser);
            newFamilyHubUser.facilityId = this.state.authModule.currentFacility
            newFamilyHubUser.residentId = data.residentId
            newFamilyHubUser.createdById = this.state.authModule.user.id,
            newFamilyHubUser.createdByType = UserTypeEnum.Staff

            const response = await firebase.app().functions(getRegionName()).httpsCallable("familyUser-create")(newFamilyHubUser)

            if (response.data.status === 200) {
                return await dispatch('getResident', newFamilyHubUser.residentId);
            } else {
                throw new Error(`Unable to create family member`)
            }
        },

        async importFamilyHubUser({state}, newFamilyHubUser) {

            const facility = this.state.facilityModule.selectedFacility

            if (newFamilyHubUser.invitedToFamilyApp) {
                newFamilyHubUser.appStatus = FamilyHubAppStatus.Pending;
            } else {
                newFamilyHubUser.appStatus = FamilyHubAppStatus.NotAdded;
            }

            const docs = (
                await firebase
                    .firestore()
                    .collection('familyHubUsers')
                    .where('email', '==', newFamilyHubUser.email)
                    .where('facilityId', '==', newFamilyHubUser.facilityId)
                    .get()
            ).docs;

            newFamilyHubUser.facilityName = this.state.authModule.currentFacilityData.name;
            newFamilyHubUser.imageUrl = DEFAULT_AVATAR_WHITE
            newFamilyHubUser.thumbnailUrl = DEFAULT_AVATAR_WHITE_200x200
            newFamilyHubUser.createdDateTime = firebase.firestore.Timestamp.fromDate(new Date())
            newFamilyHubUser.status = newFamilyHubUser.status || FamilyHubUserStatus.Active
            newFamilyHubUser.createdById = this.state.authModule.user.id,
            newFamilyHubUser.createdByType = UserTypeEnum.Staff

            let familyResidentsAccesses = null
            const residentConsent = newFamilyHubUser?.residentConsent || false
            if(Object.keys(newFamilyHubUser.familyResidentsAccesses || {}).length > 0){
                familyResidentsAccesses = {...newFamilyHubUser.familyResidentsAccesses}
            }
            else{
                familyResidentsAccesses = getDefaultFamilyResidentAccess(facility)
            }

            delete newFamilyHubUser.familyResidentsAccesses
            delete newFamilyHubUser.residentConsent

            trimStringFields(newFamilyHubUser);

            if (docs.length <= 0) {

                console.log('add member')

                newFamilyHubUser.residents[0].id = newFamilyHubUser.residentId;
                newFamilyHubUser.residents[0].name = newFamilyHubUser.residentName;

                return await firebase
                    .firestore()
                    .collection('familyHubUsers')
                    .add(newFamilyHubUser)
                    .then(async (result) => {

                        //add user to resident
                        newFamilyHubUser.id = result.id;
                        return await firebase
                            .firestore()
                            .collection('residents')
                            .doc(newFamilyHubUser.residentId)
                            .update({
                                familyHubUsers: firebase.firestore.FieldValue.arrayUnion(newFamilyHubUser),
                                familyHubUsersIds: firebase.firestore.FieldValue.arrayUnion(result.id),
                                updatedById: this.state.authModule.user.id,
                                updatedByType: UserTypeEnum.Staff,
                                typesOfRecordChanged: [`${Tier1.FamilyMembers}: ${Tier2.FamilyMember}: ${Tier3.Imports}`]
                            }).then(async () => {

                                const familyResidentsAccessesList = mapFamilyAccessSettings(familyResidentsAccesses, facility.id, newFamilyHubUser.residentId)
                            
                                for (const item of familyResidentsAccessesList) {
                                    await firebase
                                      .firestore()
                                      .collection('familyHubUsers')
                                      .doc(result.id)
                                      .collection('familyResidentAccess')
                                      .doc(`${newFamilyHubUser.residentId}_${item.name}`)
                                      .set(item, { merge: true }); 
                                  }

                                if(residentConsent === true){
                                    await firebase
                                    .firestore()
                                    .collection('familyHubUsers')
                                    .doc(result.id)
                                    .collection('familyResidentSettings')
                                    .doc(`${newFamilyHubUser.residentId}_${FamilyResidentSettings.ResidentConsent}`)
                                    .set({
                                        facilityId: facility.id,
                                        residentId: newFamilyHubUser.residentId,
                                        name: FamilyResidentSettings.ResidentConsent,
                                        type: DataTypes.Boolean,
                                        value: residentConsent
                                    }, { merge: true });
                                }
                            
                                return true
                            })
                    });
            } else if (docs.length == 1) {

                console.log('update member')
                const resident = await (await firebase.firestore().collection("residents").doc(newFamilyHubUser.residentId).get()).data()

                //updated existing user
                if (
                    (resident.familyHubUsers && resident.familyHubUsers?.length && !resident.familyHubUsers.some(x => x.email == newFamilyHubUser.email)) ||
                    !resident.familyHubUsers && !resident.familyHubUsers?.length
                ) {
                    return await firebase
                        .firestore()
                        .collection('familyHubUsers')
                        .doc(docs[0].id)
                        .update({
                            residentIds:
                                firebase.firestore.FieldValue.arrayUnion(
                                    newFamilyHubUser.residentId
                                ) || [],
                            residents:
                                firebase.firestore.FieldValue.arrayUnion({
                                    id: newFamilyHubUser.residentId,
                                    name: newFamilyHubUser.residentName,
                                    pname: newFamilyHubUser?.pname || resident?.pname || '',
                                }) || [],
                            financialEPOA:
                                firebase.firestore.FieldValue.arrayUnion({
                                    id: newFamilyHubUser.residentId,
                                    status: newFamilyHubUser.financialEPOA.find(x=>x.id == newFamilyHubUser.residentId)?.status || false,
                                }) || [],

                            welfarePOA:
                                firebase.firestore.FieldValue.arrayUnion({
                                    id: newFamilyHubUser.residentId,
                                    status: newFamilyHubUser.welfarePOA.find(x=>x.id == newFamilyHubUser.residentId)?.status || false,
                                }) || [],

                                enduringPOA:
                                firebase.firestore.FieldValue.arrayUnion({
                                    id: newFamilyHubUser.residentId,
                                    status: newFamilyHubUser.enduringPOA.find(x=>x.id == newFamilyHubUser.residentId)?.status || false,
                                }) || [],
                            })
                        .then(async () => {
                            //add user to resident
                            newFamilyHubUser.id = docs[0].id;
                            return await firebase
                                .firestore()
                                .collection('residents')
                                .doc(newFamilyHubUser.residentId)
                                .update({
                                    familyHubUsers: firebase.firestore.FieldValue.arrayUnion(newFamilyHubUser),
                                    familyHubUsersIds: firebase.firestore.FieldValue.arrayUnion(docs[0].id),
                                    updatedById: this.state.authModule.user.id,
                                    updatedByType: UserTypeEnum.Staff,
                                    typesOfRecordChanged: [`${Tier1.FamilyMembers}: ${Tier2.FamilyMember}: ${Tier3.Imports}`]
                                }).then(() => {
                                    return true
                                })
                        });
                } else {
                    console.log('User already exists in resident');
                    return Promise.resolve(false)
                }
            } else if (docs.length > 1) {
                console.error('More than one user with the same email detected');
                return Promise.reject()
            }
            return Promise.reject()
        },

        updateStoryAndPreferences({state}, {myStory, preferences}) {
            const updateData = {
                aboutMe: myStory || '',
                preferences: preferences || '',
                updatedById: this.state.authModule.user.id,
                updatedByType: UserTypeEnum.Staff,
                typesOfRecordChanged: [`${Tier1.Residents}: ${Tier2.Resident}: ${Tier3.Amend}`],
            };
            trimStringFields(updateData);
            firebase
                .firestore()
                .collection('residents')
                .doc(state.currentResident.id)
                .update(updateData);
        },

        async admitResident({dispatch}, resident) {
            resident.facilityId = this.state.authModule.currentFacility;
            resident.facilityName = this.state.authModule.currentFacilityData.name;
            resident.createdDateTime = firebase.firestore.Timestamp.fromDate(new Date())
            resident.refOrigin = 'ResHub'
            resident.createdById = this.state.authModule.user.id,
            resident.createdByType = UserTypeEnum.Staff
            resident.typesOfRecordChanged = [`${Tier1.Residents}: ${Tier2.Resident}: ${Tier3.Add}`]
            resident.orgId = this.state.facilityModule.selectedFacility.orgId

            trimStringFields(resident);

            if (resident.email) {
                resident.email = resident.email.toLowerCase()
            }

            const snap = await firebase.firestore().collection('residents').add(resident);
            await firebase.firestore().collection('residents').doc(snap.id).update({ refId: snap.id });
            return snap;
        },

        async updateResidentInfo({dispatch, state}, resident) {
            console.log("updateResidentInfo", resident)

            const residentId = resident.id

            const prevData = state.residentsShortData.find(x => x.id === residentId);

            if(resident?.hasOwnProperty('id')){
                delete resident.id
            }

            
            trimStringFields(resident);


            if (resident.email) {
                resident.email = resident.email.toLowerCase()

                if (prevData && prevData.email !== resident.email) {
                    resident.isEmailVerified = false
                }
            }

            resident.updatedById = this.state.authModule.user.id;
            resident.updatedByType = UserTypeEnum.Staff;
            resident.typesOfRecordChanged = [`${Tier1.Residents}: ${Tier2.Resident}: ${Tier3.Amend}`];

            return await firebase
                .firestore()
                .collection('residents')
                .doc(residentId)
                .update(resident)
                .then(async () => {
                    await dispatch('getFamilyMembers');
                    await dispatch("getResident", residentId)
                })

            //residents data which contains in the other documents (like familyHubUsers.residents) updating in the trigger.
        },

        async updateResidentTags({ dispatch }, data) {
            try {
                const residentRef = firebase.firestore().collection('residents').doc(data.residentId);
                const residentSnap = await residentRef.get();

                if (!residentSnap.exists) {
                throw new Error('Resident not found');
                }

                const tagTypes = ['interestsTags', 'preferencesTags', 'backgroundTags'];
                const updateData: Record<string, any> = {};

                for (const tagType of tagTypes) {
                const tagsToAdd = data[tagType].filter(tag => !tag.id && tag.name?.length > 0).map(tag => tag.name);

                const { addedTagIds } = tagsToAdd.length > 0
                    ? await dispatch('addTags', {
                        tagsNames: tagsToAdd,
                        entity: UserTypeEnum.Resident
                    })
                    : { addedTagIds: [] };

                const existingTagIds = data[tagType].filter(tag => tag.id?.length > 0).map(tag => tag.id);
                const updatedTags = (addedTagIds || []).concat(existingTagIds);

                updateData[tagType] = updatedTags;
                }

                updateData.updatedById = this.state.authModule.user.id;
                updateData.updatedByType = UserTypeEnum.Staff;
                updateData.typesOfRecordChanged = [`${Tier1.Residents}: ${Tier2.Resident}: ${Tier3.ResidentTags}`]

                await residentRef.update(updateData);

            } catch (error) {
                console.error('Error updating resident tags:', error);
            }
        },

        async sendNotificationToFamilyMember(_, payload) {
          return await firebase.app().functions(getRegionName())
                .httpsCallable('groupD-sendNotificationToFamilyMember')({
                    ...payload,
                    facilityId: this.state.authModule.currentFacility,
                    familyMemberId: payload.familyMember.id,
                });
        },

        async checkIfResidentEmailExists(_, data) {
            const emailAddress = data.emailAddress.trim().toLowerCase()

            const result = await firebase.app().functions(getRegionName())
                .httpsCallable("checkIfResidentEmailExists")({
                    emailAddress: emailAddress,
                    skipResidentId: data.skipResidentId || null
                })

            if (result.data.status === 200) {
                return result.data.data;
            }

            return true
        },

        async sendArchiveProfile(_, data) {
            console.log("sendArchiveProfile")
            await firebase
                .firestore()
                .collection('email')
                .add({
                    to: data.email,
                    facilityId: this.state.authModule.currentFacility,
                    message: {
                        subject: "Staffhub Person Data",
                        html: `<b>Name:</b> ${data.name}`,
                        attachments: data.attachments
                    }
                }).then(() => {
                    console.log("Success Send")
                })
        },

        async subscribeOnResidentsCollection({commit, state}) {
            if (state.onResidentsUnsubscribe) state.onResidentsUnsubscribe();

            const startMs = Date.now();
            const query = firebase.firestore().collection("residents").where('facilityId', '==', this.state.authModule.currentFacility)

            // Skip 'added' events from the very first snapshot since we preload residents elsewhere.
            let isInitialSnapshot = true;

            state.onResidentsUnsubscribe = query.onSnapshot((snapshot) => {
                state.isOneSubscribeOnResidents = false;
                snapshot.docChanges().forEach((change) => {

                    if (change.type === 'added') {
                        // ignore 'added' from the initial snapshot
                        if (isInitialSnapshot) return;

                        const resident: any = {id: change.doc.id, ...change.doc.data()}
                        const created = resident?.createdDateTime;
                        const createdMs = typeof created?.toMillis === 'function' ? created.toMillis() :
                        typeof created?.seconds === 'number' ? created.seconds * 1000 : null;

                        if (createdMs && createdMs >= startMs && !state.residentsShortData.some(r => r.id === change.doc.id)) {
                            const patch: any = {};
                            ResidentParams.forEach((param: string) => { patch[param] = toJsDateIfTs(resident[param]); });
                            const newResident: any = { id: change.doc.id, ...patch };
                            const next = [newResident, ...state.residentsShortData]
                            commit('setResidentsShortData', next);

                            console.log(`resident ${change.doc.id} added`, change) //log only when we really add
                        }
                    }
                    
                    if (change.type === "modified") {
                        console.log(`resident ${change.doc.id} modified`);
                        const id = change.doc.id;
                        const changeDocData = change.doc.data();
                        const idx = state.residentsShortData.findIndex(x => x.id === id);

                        if (idx !== -1) {
                            const patch: any = {};
                            ResidentParams.forEach((param: string) => {
                                if (Object.prototype.hasOwnProperty.call(changeDocData, param)) {
                                    patch[param] = toJsDateIfTs(changeDocData[param]);
                                }
                            });

                            const next = state.residentsShortData.slice();
                            next[idx] = { ...next[idx], ...patch };
                            commit("setResidentsShortData", next);
                        } else {
                            const patch: any = {};
                            ResidentParams.forEach((param: string) => { patch[param] = toJsDateIfTs(changeDocData[param]); });
                            const resident = { id, ...patch };
                            if (!state.residentsShortData.some(r => r.id === id)) {
                                commit("setResidentsShortData", [resident, ...state.residentsShortData]);
                            }
                        }
                    }

                    if (change.type === "removed") {
                        console.log(`resident ${change.doc.id} removed`, change)
                        const residents = state.residentsShortData.filter(x => x.id !== change.doc.id);
                        commit("setResidentsShortData", [...residents]);
                    }
                })

                // mark that the initial snapshot has been processed
                if (isInitialSnapshot) isInitialSnapshot = false;
            })
        },

        async unsubscribeOnResidentsCollection({state}) {
            if (state.onResidentsUnsubscribe) {
                state.onResidentsUnsubscribe();
            }
        }
    },
};
export default residentModule;
